import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Leaf, MapPin, Calendar, Clock, CreditCard, Banknote, 
  ChevronRight, ChevronLeft, Car, Zap, Wifi, Check, Search,
  X, Loader2, Plus, Trash2, Tag, Smartphone, Navigation,
  Plane, Music, Volume2, VolumeX, Thermometer, Wind, Snowflake, MessageSquare,
  LogOut, Gift, Crosshair, Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { GooglePlacesAutocomplete, GoogleMapDisplay } from '../components/GoogleMaps';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addHours, setHours, setMinutes, isBefore, startOfDay, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Promo codes (in production, this would be validated server-side)
export const BookingPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader, logout } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  
  // Multi-trip support
  const [trips, setTrips] = useState([{
    id: 1,
    pickupAddress: '',
    pickupSuggestions: [],
    pickupCoords: null,
    destAddress: '',
    destSuggestions: [],
    destCoords: null,
    showPickupSuggestions: false,
    showDestSuggestions: false,
    estimate: null,
    date: null,
    time: '',
    pinMode: false,
    pinCoords: null,
  }]);
  
  // Round trip discount
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  
  // Payment & Promo
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  
  // Wallet/Cagnotte
  const [wallet, setWallet] = useState({ balance: 0 });
  const [useWallet, setUseWallet] = useState(true);
  
  // Additional booking info
  const [flightNumber, setFlightNumber] = useState('');
  const [ambiance, setAmbiance] = useState('calme'); // calme, musique, conversation
  const [climate, setClimate] = useState('auto'); // auto, climatisation, chauffage
  const [specialRequests, setSpecialRequests] = useState('');

  // Fetch wallet balance on load
  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const response = await axios.get(`${API}/user/wallet`, {
          headers: getAuthHeader()
        });
        setWallet({ balance: response.data.wallet_balance || 0 });
      } catch (error) {
        console.error('Error fetching wallet:', error);
      }
    };
    fetchWallet();
  }, [getAuthHeader]);

  // Load selected airport transfer if any
  useEffect(() => {
    const selectedTransfer = sessionStorage.getItem('selectedTransfer');
    if (selectedTransfer) {
      const transfer = JSON.parse(selectedTransfer);
      setTrips([{
        ...trips[0],
        pickupAddress: transfer.from,
        destAddress: transfer.to,
      }]);
      sessionStorage.removeItem('selectedTransfer');
    }
  }, []);

  // Debounce search
  const searchAddress = useCallback(async (query, tripIndex, isPickup) => {
    if (query.length < 3) return;

    try {
      const response = await axios.get(`${API}/geocode/search`, {
        params: { q: query }
      });
      
      setTrips(prev => prev.map((trip, i) => {
        if (i !== tripIndex) return trip;
        return {
          ...trip,
          [isPickup ? 'pickupSuggestions' : 'destSuggestions']: response.data,
          [isPickup ? 'showPickupSuggestions' : 'showDestSuggestions']: true,
        };
      }));
    } catch (error) {
      console.error('Search error:', error);
    }
  }, []);

  // Get price estimate for a trip - called with explicit coordinates
  const getEstimateWithCoords = useCallback(async (tripIndex, pickupCoords, destCoords, pickupAddress, destAddress) => {
    if (!pickupCoords || !destCoords) {
      console.log('getEstimateWithCoords: Missing coords', { pickupCoords, destCoords });
      return;
    }

    console.log('getEstimateWithCoords called:', { tripIndex, pickupCoords, destCoords });
    setEstimating(true);
    try {
      // Get pickup hour and date from the trip data for accurate pricing
      const trip = trips[tripIndex];
      let pickupHour = new Date().getHours();
      let pickupDateStr = '';
      if (trip?.time) {
        try { pickupHour = parseInt(trip.time.split(':')[0]); } catch {}
      }
      if (trip?.date) {
        try {
          const d = new Date(trip.date);
          pickupDateStr = d.toISOString().split('T')[0];
        } catch {}
      }

      const response = await axios.post(`${API}/bookings/estimate`, null, {
        params: {
          pickup_lat: pickupCoords.lat,
          pickup_lng: pickupCoords.lng,
          dest_lat: destCoords.lat,
          dest_lng: destCoords.lng,
          pickup_address: pickupAddress || '',
          dest_address: destAddress || '',
          pickup_hour: pickupHour,
          pickup_date: pickupDateStr,
          phone: user?.phone || '',
        }
      });
      
      console.log('Estimate response:', response.data);
      
      setTrips(prev => prev.map((t, i) => {
        if (i !== tripIndex) return t;
        return { ...t, estimate: response.data };
      }));
    } catch (error) {
      console.error('Estimate error:', error);
      toast.error('Impossible de calculer le prix');
    } finally {
      setEstimating(false);
    }
  }, [user?.phone]);

  // Legacy getEstimate for auto-fetch useEffect (reads from current state)
  const getEstimate = useCallback(async (tripIndex) => {
    const trip = trips[tripIndex];
    if (!trip?.pickupCoords || !trip?.destCoords) return;
    
    await getEstimateWithCoords(
      tripIndex, 
      trip.pickupCoords, 
      trip.destCoords, 
      trip.pickupAddress, 
      trip.destAddress
    );
  }, [trips, getEstimateWithCoords]);

  // Auto-fetch estimate when both coords are set but no estimate
  useEffect(() => {
    trips.forEach((trip, index) => {
      if (trip.pickupCoords && trip.destCoords && !trip.estimate && !estimating) {
        console.log(`Auto-fetching estimate for trip ${index}`);
        getEstimate(index);
      }
    });
  }, [trips, estimating, getEstimate]);

  const updateTripField = (tripIndex, field, value) => {
    setTrips(prev => prev.map((trip, i) => {
      if (i !== tripIndex) return trip;
      
      // Reset coords if address is being typed
      if (field === 'pickupAddress' || field === 'destAddress') {
        const coordsField = field === 'pickupAddress' ? 'pickupCoords' : 'destCoords';
        return { ...trip, [field]: value, [coordsField]: null, estimate: null };
      }
      
      return { ...trip, [field]: value };
    }));
  };

  const selectAddress = (tripIndex, suggestion, isPickup) => {
    console.log(`Selecting ${isPickup ? 'pickup' : 'dest'} for trip ${tripIndex}:`, suggestion);
    
    setTrips(prev => prev.map((trip, i) => {
      if (i !== tripIndex) return trip;
      
      const newTrip = {
        ...trip,
        [isPickup ? 'pickupAddress' : 'destAddress']: suggestion.display_name,
        [isPickup ? 'pickupCoords' : 'destCoords']: { 
          lat: parseFloat(suggestion.lat), 
          lng: parseFloat(suggestion.lng) 
        },
        [isPickup ? 'showPickupSuggestions' : 'showDestSuggestions']: false,
        // Clear estimate when address changes to trigger re-fetch
        estimate: isPickup && trip.destCoords ? null : (!isPickup && trip.pickupCoords ? null : trip.estimate),
      };
      
      console.log('Updated trip:', newTrip);
      return newTrip;
    }));
  };

  const addTrip = () => {
    setTrips(prev => [...prev, {
      id: Date.now(),
      pickupAddress: '',
      pickupSuggestions: [],
      pickupCoords: null,
      destAddress: '',
      destSuggestions: [],
      destCoords: null,
      showPickupSuggestions: false,
      showDestSuggestions: false,
      estimate: null,
      date: null,
      time: '',
    }]);
  };

  const removeTrip = (tripIndex) => {
    if (trips.length === 1) return;
    setTrips(prev => prev.filter((_, i) => i !== tripIndex));
  };

  const applyPromoCode = async () => {
    const code = promoCode.toUpperCase().trim();
    if (!code) {
      setPromoError('Entrez un code promo');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/promo/validate?code=${encodeURIComponent(code)}`,
        null,
        { headers: getAuthHeader() }
      );
      
      if (response.data.valid) {
        setAppliedPromo({ code, ...response.data });
        setPromoError('');
        toast.success(`Code promo "${code}" appliqué !`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Code promo invalide';
      setPromoError(message);
      setAppliedPromo(null);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 5) {
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        slots.push(`${hour}:${minute}`);
      }
    }
    return slots;
  };

  const isValidDateTime = (trip) => {
    if (!trip.date || !trip.time) return false;
    
    const [hours, minutes] = trip.time.split(':').map(Number);
    const selectedDateTime = setMinutes(setHours(trip.date, hours), minutes);
    const minDateTime = addHours(new Date(), 1);
    
    return !isBefore(selectedDateTime, minDateTime);
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = trips.reduce((sum, trip) => {
      const price = trip.estimate?.price || trip.estimate?.breakdown?.total_ttc || 0;
      return sum + price;
    }, 0);
    let roundTripDiscount = 0;
    let promoDiscount = 0;

    // Check for round trip discount (10% if same day)
    if (trips.length >= 2 && isRoundTrip) {
      const firstTrip = trips[0];
      const hasReturnSameDay = trips.some((trip, i) => {
        if (i === 0) return false;
        return trip.date && firstTrip.date && isSameDay(trip.date, firstTrip.date);
      });
      
      if (hasReturnSameDay) {
        roundTripDiscount = subtotal * 0.10;
      }
    }

    // Apply promo code
    if (appliedPromo) {
      if (appliedPromo.type === 'percent') {
        promoDiscount = (subtotal - roundTripDiscount) * (appliedPromo.discount / 100);
      } else {
        promoDiscount = Math.min(appliedPromo.discount, subtotal - roundTripDiscount);
      }
    }

    const total = subtotal - roundTripDiscount - promoDiscount;
    
    // Calculate wallet deduction
    let walletDeduction = 0;
    if (useWallet && wallet.balance > 0) {
      walletDeduction = Math.min(wallet.balance, Math.max(total, 0));
    }
    
    const finalTotal = Math.max(total - walletDeduction, 0);

    return {
      subtotal,
      roundTripDiscount,
      promoDiscount,
      walletDeduction,
      total: finalTotal,
      originalTotal: Math.max(total, 0),
    };
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return trips.every(trip => trip.pickupCoords && trip.destCoords && trip.estimate);
      case 2:
        return trips.every(trip => isValidDateTime(trip));
      case 3:
        return paymentMethod;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    
    setLoading(true);
    try {
      // Create bookings for all trips
      const bookings = [];
      for (const trip of trips) {
        const [hours, minutes] = trip.time.split(':').map(Number);
        const pickupDateTime = setMinutes(setHours(trip.date, hours), minutes);
        
        const response = await axios.post(
          `${API}/bookings`,
          {
            pickup_address: trip.pickupAddress,
            pickup_lat: trip.pickupCoords.lat,
            pickup_lng: trip.pickupCoords.lng,
            destination_address: trip.destAddress,
            destination_lat: trip.destCoords.lat,
            destination_lng: trip.destCoords.lng,
            pickup_datetime: format(pickupDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
            payment_method: paymentMethod,
            promo_code: appliedPromo?.code || null,
            use_wallet: useWallet,
            flight_number: flightNumber || null,
            ambiance: ambiance,
            climate: climate,
            special_requests: specialRequests || null
          },
          { headers: getAuthHeader() }
        );
        bookings.push(response.data);
      }

      const firstBooking = bookings[0];
      
      // If price is 0 (fully paid by wallet), skip payment
      if (firstBooking.price <= 0 || firstBooking.payment_status === 'paid') {
        toast.success('Réservation confirmée ! (payée avec votre cagnotte)');
        navigate(`/booking/confirmation?booking_id=${firstBooking.id}`);
        return;
      }

      if (paymentMethod === 'stripe' || paymentMethod === 'apple_pay' || paymentMethod === 'google_pay' || paymentMethod === 'paypal') {
        // Create checkout session
        const checkoutResponse = await axios.post(
          `${API}/payments/checkout`,
          null,
          {
            params: {
              booking_id: firstBooking.id,
              origin_url: window.location.origin
            },
            headers: getAuthHeader()
          }
        );
        
        // Redirect to Stripe
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        // For cash/card on board payments
        toast.success('Réservation confirmée !');
        navigate(`/booking/confirmation?booking_id=${firstBooking.id}`);
      }
    } catch (error) {
      console.error('Booking error:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la réservation';
      toast.error(message);
      
      if (error.response?.status === 401) {
        logout();
        navigate('/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();
  const minDate = startOfDay(addHours(new Date(), 5));

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="px-6 py-6 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="logo-home-btn"
          >
            <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-14 h-14" />
            <span className="font-bold text-xl font-['Outfit'] text-white">YOUMI GREEN</span>
            <span className="text-emerald-500 font-bold text-xl font-['Outfit']">VTC</span>
          </button>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-zinc-400 text-sm hidden md:block">{t('booking_hello')}, {user?.first_name}</span>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-zinc-400 hover:text-white hidden sm:flex"
              onClick={() => navigate('/dashboard')}
              data-testid="my-bookings-btn"
            >
              {t('booking_my_bookings')}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={() => { logout(); navigate('/'); }}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Progress */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                      s <= step 
                        ? 'bg-emerald-500 text-black' 
                        : 'bg-zinc-800 text-zinc-500'
                    }`}
                  >
                    {s < step ? <Check className="w-5 h-5" /> : s}
                  </div>
                  {s < 3 && (
                    <div 
                      className={`w-24 md:w-32 h-1 mx-2 rounded-full transition-colors ${
                        s < step ? 'bg-emerald-500' : 'bg-zinc-800'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm">
              <span className={step >= 1 ? 'text-emerald-500' : 'text-zinc-500'}>{t('booking_step_route')}</span>
              <span className={step >= 2 ? 'text-emerald-500' : 'text-zinc-500'}>{t('booking_step_datetime')}</span>
              <span className={step >= 3 ? 'text-emerald-500' : 'text-zinc-500'}>{t('booking_step_payment')}</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <div className="glass-card rounded-2xl p-8">
                {/* Step 1: Routes */}
                {step === 1 && (
                  <div className="space-y-6 animate-fade-in-up">
                    {/* Privileged client welcome banner */}
                    {trips.some(t => t.estimate?.is_privileged_price) && (
                      <div
                        className="rounded-2xl p-5 bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-emerald-500/10 border border-amber-400/40 shadow-[0_0_25px_-5px_rgba(251,191,36,0.35)]"
                        data-testid="privileged-welcome-banner"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center shrink-0">
                            <Leaf className="w-5 h-5 text-amber-300" />
                          </div>
                          <div className="flex-1">
                            <p className="text-amber-200 font-semibold text-base" data-testid="privileged-welcome-text">
                              Bienvenue {(() => {
                                const t = trips.find(tr => tr.estimate?.is_privileged_price);
                                return t?.estimate?.privileged_client_name || (user?.first_name ? `${user.first_name}` : '');
                              })()} — {' '}
                              <span className="text-emerald-300">
                                Votre tarif privilégié de {(() => {
                                  const t = trips.find(tr => tr.estimate?.is_privileged_price);
                                  return Number(t?.estimate?.price || 0).toFixed(2);
                                })()}€ a été appliqué
                              </span>
                            </p>
                            <p className="text-xs text-zinc-400 mt-1">
                              Tarif négocié appliqué automatiquement, sans supplément nuit ni saisonnalité.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-white font-['Outfit']">
                        Vos trajets
                      </h2>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addTrip}
                        className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                        data-testid="add-trip-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('booking_add_trip')}
                      </Button>
                    </div>

                    {trips.map((trip, tripIndex) => (
                      <div key={trip.id} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm font-medium text-emerald-500">
                            Trajet {tripIndex + 1}
                          </span>
                          {trips.length > 1 && (
                            <button
                              onClick={() => removeTrip(tripIndex)}
                              className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Supprimer
                            </button>
                          )}
                        </div>

                        {/* Pickup Address - Google Places */}
                        <div className="space-y-2 mb-4">
                          <Label className="text-zinc-300 flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-emerald-500" />
                            {t('booking_pickup')}
                          </Label>
                          <GooglePlacesAutocomplete
                            placeholder="Ex: Aéroport Nice Côte d'Azur"
                            value={trip.pickupAddress}
                            icon={MapPin}
                            onSelect={(result) => {
                              const newPickupCoords = { lat: result.lat, lng: result.lng };
                              const newPickupAddress = result.display_name;
                              
                              // Update state
                              setTrips(prev => prev.map((t, i) => {
                                if (i !== tripIndex) return t;
                                return {
                                  ...t,
                                  pickupAddress: newPickupAddress,
                                  pickupCoords: newPickupCoords,
                                  estimate: null, // Reset estimate
                                };
                              }));
                              
                              // If destination exists, immediately calculate estimate with both coords
                              if (trip.destCoords) {
                                getEstimateWithCoords(
                                  tripIndex, 
                                  newPickupCoords, 
                                  trip.destCoords, 
                                  newPickupAddress, 
                                  trip.destAddress
                                );
                              }
                            }}
                          />
                          {trip.pickupCoords && (
                            <div className="flex items-center gap-2 text-xs text-emerald-500">
                              <Check className="w-3 h-3" />
                              Adresse sélectionnée
                            </div>
                          )}
                        </div>

                        {/* Destination Address - Google Places */}
                        <div className="space-y-2 mb-4">
                          <Label className="text-zinc-300 flex items-center gap-2">
                            <Navigation className="w-4 h-4 text-red-500" />
                            Destination
                          </Label>
                          <GooglePlacesAutocomplete
                            placeholder="Ex: Cannes Centre"
                            value={trip.destAddress}
                            icon={Navigation}
                            onSelect={(result) => {
                              const newDestCoords = { lat: result.lat, lng: result.lng };
                              const newDestAddress = result.display_name;
                              
                              // Update state
                              setTrips(prev => prev.map((t, i) => {
                                if (i !== tripIndex) return t;
                                return {
                                  ...t,
                                  destAddress: newDestAddress,
                                  destCoords: newDestCoords,
                                  estimate: null, // Reset estimate
                                };
                              }));
                              
                              // If pickup exists, immediately calculate estimate with both coords
                              if (trip.pickupCoords) {
                                getEstimateWithCoords(
                                  tripIndex, 
                                  trip.pickupCoords, 
                                  newDestCoords, 
                                  trip.pickupAddress, 
                                  newDestAddress
                                );
                              }
                            }}
                          />
                          {trip.destCoords && (
                            <div className="flex items-center gap-2 text-xs text-emerald-500">
                              <Check className="w-3 h-3" />
                              Adresse sélectionnée
                            </div>
                          )}
                        </div>

                        {/* Google Map Display */}
                        {(trip.pickupCoords || trip.destCoords) && (
                          <div className="mb-4 space-y-2">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setTrips(prev => prev.map((t, j) => j === tripIndex ? { ...t, pinMode: !t.pinMode, pinCoords: t.pinMode ? null : t.pinCoords } : t))}
                                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                  trip.pinMode ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                                }`}
                                data-testid={`toggle-pin-mode-trip-${tripIndex}`}
                              >
                                <Crosshair className="w-3.5 h-3.5" />
                                {trip.pinMode ? 'Repere actif' : 'Placer un repere'}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!navigator.geolocation) return;
                                  navigator.geolocation.getCurrentPosition((pos) => {
                                    const lat = pos.coords.latitude;
                                    const lng = pos.coords.longitude;
                                    setTrips(prev => prev.map((t, j) => j === tripIndex ? { ...t, pinMode: true, pinCoords: { lat, lng, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` }, pickupCoords: { lat, lng } } : t));
                                    if (window.google) {
                                      const geocoder = new window.google.maps.Geocoder();
                                      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                                        if (status === 'OK' && results[0]) {
                                          setTrips(prev => prev.map((t, j) => j === tripIndex ? { ...t, pinCoords: { lat, lng, address: results[0].formatted_address }, pickupAddress: results[0].formatted_address } : t));
                                        }
                                      });
                                    }
                                    toast.success('Position detectee');
                                  }, () => toast.error('Position non disponible'), { enableHighAccuracy: true });
                                }}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors"
                                data-testid={`use-location-trip-${tripIndex}`}
                              >
                                <Navigation className="w-3.5 h-3.5" /> Ma position
                              </button>
                            </div>
                            {trip.pinCoords && (
                              <div className="flex items-center gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                                <Crosshair className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                <span className="text-emerald-300 truncate">{trip.pinCoords.address}</span>
                                <button type="button" onClick={() => setTrips(prev => prev.map((t, j) => j === tripIndex ? { ...t, pinCoords: null, pinMode: false } : t))} className="text-zinc-400 hover:text-red-400 ml-auto shrink-0">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                            <GoogleMapDisplay
                              pickupCoords={trip.pickupCoords}
                              destCoords={trip.destCoords}
                              height="200px"
                              showRoute={trip.pickupCoords && trip.destCoords}
                              interactive={trip.pinMode}
                              pinCoords={trip.pinCoords}
                              onPinPlace={(pin) => {
                                setTrips(prev => prev.map((t, j) => j === tripIndex ? { ...t, pinCoords: pin, pickupAddress: pin.address, pickupCoords: { lat: pin.lat, lng: pin.lng }, estimate: null } : t));
                              }}
                            />
                          </div>
                        )}

                        {/* Trip estimate */}
                        {trip.estimate && (
                          <div className="mt-4 p-3 bg-zinc-800 rounded-lg flex justify-between items-center">
                            <span className="text-zinc-400 text-sm">
                              {trip.estimate.distance_km.toFixed(1)} km • ~{Math.round(trip.estimate.duration_min)} min
                            </span>
                            <span className="text-lg font-bold text-emerald-500">
                              {trip.estimate.price.toFixed(2)}€
                            </span>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Round trip checkbox */}
                    {trips.length >= 2 && (
                      <div className="flex items-center space-x-2 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                        <Checkbox
                          id="roundTrip"
                          checked={isRoundTrip}
                          onCheckedChange={setIsRoundTrip}
                          className="border-emerald-500"
                        />
                        <label
                          htmlFor="roundTrip"
                          className="text-sm font-medium text-emerald-400 cursor-pointer"
                        >
                          {t('booking_round_trip')}
                        </label>
                      </div>
                    )}

                    {/* Vehicle Info */}
                    <div className="mt-6 p-6 bg-zinc-900/50 rounded-xl border border-zinc-800">
                      <div className="flex items-center gap-4">
                        <img 
                          src="https://images.unsplash.com/photo-1696276939049-15504f8b5d48?crop=entropy&cs=srgb&fm=jpg&w=200"
                          alt="Tesla Model Y"
                          className="w-24 h-16 object-cover rounded-lg"
                        />
                        <div>
                          <h3 className="font-bold text-white font-['Outfit']">Tesla Model Y</h3>
                          <p className="text-sm text-zinc-400">Blanche • Intérieur Rouge • 4 passagers</p>
                          <div className="flex gap-3 mt-2">
                            <span className="eco-badge text-[10px] py-0.5">
                              <Zap className="w-3 h-3" /> Électrique
                            </span>
                            <span className="eco-badge text-[10px] py-0.5">
                              <Wifi className="w-3 h-3" /> Wi-Fi
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Date/Time */}
                {step === 2 && (
                  <div className="space-y-6 animate-fade-in-up">
                    <h2 className="text-2xl font-bold text-white font-['Outfit']">
                      {t('booking_date_time')}
                    </h2>
                    <p className="text-zinc-400">
                      Réservation minimum 1 heure à l'avance
                    </p>

                    {trips.map((trip, tripIndex) => (
                      <div key={trip.id} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                        <span className="text-sm font-medium text-emerald-500 mb-4 block">
                          Trajet {tripIndex + 1}: {trip.pickupAddress?.split(',')[0]} → {trip.destAddress?.split(',')[0]}
                        </span>

                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Date Picker */}
                          <div className="space-y-2">
                            <Label className="text-zinc-300 flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-emerald-500" />
                              Date
                            </Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full justify-start text-left bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                                  data-testid={`date-picker-${tripIndex}`}
                                >
                                  {trip.date ? format(trip.date, 'PPP', { locale: fr }) : 'Sélectionner'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700">
                                <CalendarComponent
                                  mode="single"
                                  selected={trip.date}
                                  onSelect={(date) => updateTripField(tripIndex, 'date', date)}
                                  disabled={(d) => isBefore(d, minDate)}
                                  initialFocus
                                  className="bg-zinc-900"
                                />
                              </PopoverContent>
                            </Popover>
                          </div>

                          {/* Time Picker */}
                          <div className="space-y-2">
                            <Label className="text-zinc-300 flex items-center gap-2">
                              <Clock className="w-4 h-4 text-emerald-500" />
                              Heure
                            </Label>
                            <select
                              value={trip.time}
                              onChange={(e) => updateTripField(tripIndex, 'time', e.target.value)}
                              className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                              data-testid={`time-select-${tripIndex}`}
                            >
                              <option value="">Sélectionner</option>
                              {generateTimeSlots().map((slot) => (
                                <option key={slot} value={slot}>{slot}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {trip.date && trip.time && !isValidDateTime(trip) && (
                          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                            La réservation doit être faite au moins 1 heure à l'avance
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Additional Trip Information */}
                    <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800 space-y-4">
                      <h3 className="text-lg font-bold text-white font-['Outfit'] flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-emerald-500" />
                        Informations complémentaires
                      </h3>

                      {/* Flight Number */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300 flex items-center gap-2">
                          <Plane className="w-4 h-4 text-emerald-500" />
                          Numéro de vol (optionnel)
                        </Label>
                        <Input
                          value={flightNumber}
                          onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
                          placeholder="Ex: AF1234"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="flight-number-input"
                        />
                        <p className="text-xs text-zinc-500">Pour les trajets depuis/vers l'aéroport, nous suivrons votre vol en temps réel</p>
                      </div>

                      {/* Ambiance */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300 flex items-center gap-2">
                          <Music className="w-4 h-4 text-emerald-500" />
                          Ambiance souhaitée
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: 'calme', label: 'Calme', icon: VolumeX },
                            { value: 'musique', label: 'Musique', icon: Music },
                            { value: 'conversation', label: 'Conversation', icon: Volume2 }
                          ].map(({ value, label, icon: Icon }) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setAmbiance(value)}
                              className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                                ambiance === value
                                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                              }`}
                              data-testid={`ambiance-${value}`}
                            >
                              <Icon className="w-5 h-5" />
                              <span className="text-sm">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Climate */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300 flex items-center gap-2">
                          <Thermometer className="w-4 h-4 text-emerald-500" />
                          Climatisation / Chauffage
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: 'auto', label: 'Auto', icon: Wind },
                            { value: 'climatisation', label: 'Clim', icon: Snowflake },
                            { value: 'chauffage', label: 'Chauffage', icon: Thermometer }
                          ].map(({ value, label, icon: Icon }) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setClimate(value)}
                              className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                                climate === value
                                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                              }`}
                              data-testid={`climate-${value}`}
                            >
                              <Icon className="w-5 h-5" />
                              <span className="text-sm">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Special Requests */}
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Demandes spéciales (optionnel)</Label>
                        <textarea
                          value={specialRequests}
                          onChange={(e) => setSpecialRequests(e.target.value)}
                          placeholder="Siège bébé, bagages volumineux, assistance mobilité..."
                          className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 resize-none"
                          rows={2}
                          data-testid="special-requests-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Payment */}
                {step === 3 && (
                  <div className="space-y-6 animate-fade-in-up">
                    <h2 className="text-2xl font-bold text-white font-['Outfit']">
                      {t('booking_payment_method')}
                    </h2>

                    <RadioGroup 
                      value={paymentMethod} 
                      onValueChange={setPaymentMethod}
                      className="space-y-3"
                    >
                      <label 
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                          paymentMethod === 'stripe' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" />
                        <CreditCard className="w-5 h-5 text-zinc-400" />
                        <div className="flex-1">
                          <p className="font-medium text-white">{t('booking_card')}</p>
                          <p className="text-sm text-zinc-400">{t('booking_card_stripe')}</p>
                        </div>
                      </label>

                      <label 
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                          paymentMethod === 'apple_pay' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <RadioGroupItem value="apple_pay" id="apple_pay" data-testid="payment-apple" />
                        <Smartphone className="w-5 h-5 text-zinc-400" />
                        <div className="flex-1">
                          <p className="font-medium text-white">Apple Pay</p>
                          <p className="text-sm text-zinc-400">Paiement rapide et sécurisé</p>
                        </div>
                      </label>

                      <label 
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                          paymentMethod === 'google_pay' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <RadioGroupItem value="google_pay" id="google_pay" data-testid="payment-google" />
                        <Smartphone className="w-5 h-5 text-zinc-400" />
                        <div className="flex-1">
                          <p className="font-medium text-white">Google Pay</p>
                          <p className="text-sm text-zinc-400">Paiement rapide et sécurisé</p>
                        </div>
                      </label>

                      <label 
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                          paymentMethod === 'paypal' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" />
                        <CreditCard className="w-5 h-5 text-zinc-400" />
                        <div className="flex-1">
                          <p className="font-medium text-white">PayPal</p>
                          <p className="text-sm text-zinc-400">Paiement via votre compte PayPal</p>
                        </div>
                      </label>

                      <label 
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                          paymentMethod === 'card_onboard' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <RadioGroupItem value="card_onboard" id="card_onboard" data-testid="payment-card" />
                        <CreditCard className="w-5 h-5 text-zinc-400" />
                        <div className="flex-1">
                          <p className="font-medium text-white">{t('booking_card_onboard')}</p>
                          <p className="text-sm text-zinc-400">{t('booking_pay_driver')}</p>
                        </div>
                      </label>

                      <label 
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                          paymentMethod === 'cash' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <RadioGroupItem value="cash" id="cash" data-testid="payment-cash" />
                        <Banknote className="w-5 h-5 text-zinc-400" />
                        <div className="flex-1">
                          <p className="font-medium text-white">{t('booking_cash')}</p>
                          <p className="text-sm text-zinc-400">{t('booking_pay_driver')}</p>
                        </div>
                      </label>
                    </RadioGroup>

                    {/* Promo Code */}
                    <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                      <Label className="text-zinc-300 flex items-center gap-2 mb-3">
                        <Tag className="w-4 h-4 text-emerald-500" />
                        {t('booking_promo_code')}
                      </Label>
                      
                      {appliedPromo ? (
                        <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                          <div>
                            <span className="text-emerald-400 font-medium">{appliedPromo.code}</span>
                            <p className="text-sm text-emerald-400/70">{appliedPromo.description}</p>
                          </div>
                          <button
                            onClick={removePromoCode}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value);
                              setPromoError('');
                            }}
                            placeholder="Entrez votre code"
                            className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                            data-testid="promo-code-input"
                          />
                          <Button
                            onClick={applyPromoCode}
                            variant="outline"
                            className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                            data-testid="apply-promo-btn"
                          >
                            Appliquer
                          </Button>
                        </div>
                      )}
                      {promoError && (
                        <p className="text-red-400 text-sm mt-2">{promoError}</p>
                      )}
                    </div>

                    {/* Wallet / Cagnotte */}
                    {wallet.balance > 0 && (
                      <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                              <Gift className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">Ma Cagnotte</p>
                              <p className="text-purple-400 text-lg font-bold">{wallet.balance.toFixed(2)}€</p>
                            </div>
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useWallet}
                              onChange={(e) => setUseWallet(e.target.checked)}
                              className="w-5 h-5 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
                              data-testid="use-wallet-checkbox"
                            />
                            <span className="text-zinc-300 text-sm">Utiliser</span>
                          </label>
                        </div>
                        {useWallet && (
                          <p className="text-purple-300/70 text-sm mt-2">
                            {calculateTotals().walletDeduction.toFixed(2)}€ seront déduits de votre course
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between mt-8 pt-6 border-t border-zinc-800">
                  {step > 1 ? (
                    <Button 
                      variant="ghost" 
                      onClick={() => setStep(step - 1)}
                      className="text-zinc-400 hover:text-white"
                      data-testid="prev-step-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      {t('booking_back')}
                    </Button>
                  ) : (
                    <div />
                  )}
                  
                  <Button 
                    className="btn-primary"
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    data-testid="next-step-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {step === 3 ? t('booking_confirm') : t('booking_continue')}
                    {step < 3 && <ChevronRight className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="lg:col-span-1">
              <div className="glass-card rounded-2xl p-6 sticky top-6">
                <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">
                  {t('booking_summary')}
                </h3>

                {estimating ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                  </div>
                ) : trips.some(t => t.estimate) ? (
                  <>
                    <div className="space-y-3 mb-6">
                      {trips.map((trip, i) => trip.estimate && (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-zinc-400">{t('booking_trip')} {i + 1}</span>
                          <span className="text-white">{trip.estimate.price?.toFixed(2) || trip.estimate.breakdown?.total_ttc?.toFixed(2)}€</span>
                        </div>
                      ))}
                      
                      {totals.roundTripDiscount > 0 && (
                        <div className="flex justify-between text-sm text-emerald-400">
                          <span>{t('booking_round_trip_discount')}</span>
                          <span>-{totals.roundTripDiscount.toFixed(2)}€</span>
                        </div>
                      )}
                      
                      {totals.promoDiscount > 0 && (
                        <div className="flex justify-between text-sm text-emerald-400">
                          <span>{t('booking_promo_code')} ({appliedPromo?.code})</span>
                          <span>-{totals.promoDiscount.toFixed(2)}€</span>
                        </div>
                      )}
                      
                      {totals.walletDeduction > 0 && (
                        <div className="flex justify-between text-sm text-purple-400">
                          <span>{t('booking_wallet_used')}</span>
                          <span>-{totals.walletDeduction.toFixed(2)}€</span>
                        </div>
                      )}
                    </div>

                    {/* HT/TTC breakdown */}
                    <div className="border-t border-zinc-700 pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">{t('booking_total_ht')}</span>
                        <span className="text-white">{(totals.originalTotal / 1.10).toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">{t('booking_vat')}</span>
                        <span className="text-white">{(totals.originalTotal - totals.originalTotal / 1.10).toFixed(2)}€</span>
                      </div>
                      {totals.walletDeduction > 0 && (
                        <div className="flex justify-between text-sm text-purple-400">
                          <span>{t('booking_wallet_used')}</span>
                          <span>-{totals.walletDeduction.toFixed(2)}€</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                        <span className="text-zinc-300 font-medium">{t('booking_to_pay')}</span>
                        <span className="text-3xl font-bold text-emerald-500 font-['Outfit']">
                          {totals.total.toFixed(2)}€
                        </span>
                      </div>
                      {totals.total === 0 && (
                        <p className="text-center text-purple-400 text-sm mt-2">
                          {t('booking_paid_wallet')}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('booking_enter_addresses')}</p>
                  </div>
                )}

                <div className="mt-6 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm">
                    <Leaf className="w-4 h-4" />
                    <span>{t('booking_co2')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <WhatsAppButton />
    </div>
  );
};
